/* eslint no-console: 0 */
import { HotKeys } from 'react-hotkeys';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import colors, { defaults } from 'shared-assets/lib/colors';

const Ul = styled.ul`
  && {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 1rem;
  }
`;

const Li = styled.li`
  margin: 0;
`;

const Button = styled.button`
  && {
    user-select: none;
    background-color: transparent;
    font-family: var(--font-family, ${defaults.fontFamily});
    padding: 6px 10px;
    border: none;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    -webkit-font-smoothing: antialiased;
    text-decoration: none;
    outline: 0;
    line-height: 1.5rem;
    color: ${colors.link};

    &:hover {
      background-color: rgba(${colors.secondaryRGB.join(', ')}, 0.05);
      border-radius: 3px;
    }

    &.activePage {
      font-weight: bold;
      color: ${colors.secondary};
    }

    &:disabled,
    &:disabled:hover,
    &:disabled:focus {
      font-weight: normal;
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const keyboardMap = {
  previousPage: 'left',
  nextPage: 'right',
};

class Pagination extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      cursorStart: 0,
      currentPage: props.currentPage || props.defaultCurrentPage || 1,
    };
  }

  static getDerivedStateFromProps(props) {
    if (props.defaultCurrentPage && props.currentPage) {
      console.error(
        'Warning: Pagination contains both currentPage and defaultCurrentPage props. Pagination components must be either controlled or uncontrolled (specify either the currentPage prop, or the defaultCurrentPage prop, but not both). Decide between using a controlled or uncontrolled Pagination component and remove one of these props.',
      );
    }
    if (props.currentPage > props.totalPages) {
      console.error(
        'Warning: Pagination prop `currentPage` is greater than prop `totalPages`',
      );
    }
    if (props.currentPage) {
      let cursorStart =
        props.currentPage / props.maxPageButtons > 1
          ? Math.floor(props.currentPage / props.maxPageButtons) *
            props.maxPageButtons
          : 0;
      if (cursorStart === props.currentPage) {
        cursorStart =
          (props.currentPage / props.maxPageButtons - 1) * props.maxPageButtons;
      }
      return {
        currentPage: props.currentPage,
        cursorStart,
      };
    }
    return null;
  }

  static defaultProps = {
    currentPage: undefined,
    defaultCurrentPage: undefined,
    maxPageButtons: 5,
  };

  static propTypes = {
    currentPage: PropTypes.number,
    defaultCurrentPage: PropTypes.number,
    totalPages: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    maxPageButtons: PropTypes.number,
  };

  handlePageChange(pageNumber) {
    this.props.onChange(pageNumber);
    if (this.props.currentPage === undefined) {
      this.setState({
        currentPage: pageNumber,
      });
    }
  }

  handleEllipsesPrevious() {
    this.props.onChange(this.state.cursorStart);
    this.setState({
      cursorStart: Math.max(
        this.state.cursorStart - this.props.maxPageButtons,
        0,
      ),
      currentPage: this.state.cursorStart,
    });
  }

  handleEllipsesNext() {
    this.props.onChange(this.state.cursorStart + this.props.maxPageButtons + 1);
    this.setState({
      cursorStart: this.state.cursorStart + this.props.maxPageButtons,
      currentPage: this.state.cursorStart + this.props.maxPageButtons + 1,
    });
  }

  handleFirstPage = () => {
    this.props.onChange(1);
    this.setState({
      currentPage: 1,
      cursorStart: 0,
    });
  };

  handleLastPage = () => {
    this.props.onChange(this.props.totalPages);
    if (
      this.props.totalPages - this.state.cursorStart >=
      this.props.maxPageButtons
    ) {
      this.setState({
        currentPage: this.props.totalPages,
        cursorStart: Math.max(
          this.props.totalPages - this.props.maxPageButtons,
          0,
        ),
      });
    } else {
      this.setState({
        currentPage: this.props.totalPages,
      });
    }
  };

  handlePreviousPage = () => {
    const currentPage = this.state.currentPage;
    const previousPage = Math.max(this.state.currentPage - 1, 1);
    this.props.onChange(previousPage);
    this.setState({
      currentPage: previousPage,
    });
    if (this.state.cursorStart === currentPage - 1) {
      this.setState({
        cursorStart: Math.max(currentPage - this.props.maxPageButtons - 1, 0),
      });
    }
  };

  handleNextPage = () => {
    const currentPage = this.state.currentPage;
    const nextPage = Math.min(
      this.state.currentPage + 1,
      this.props.totalPages,
    );
    this.props.onChange(nextPage);
    this.setState({
      currentPage: nextPage,
    });
    if (this.state.cursorStart === currentPage - this.props.maxPageButtons) {
      this.setState({
        cursorStart: currentPage,
      });
    }
  };

  arrayofPageButtons() {
    let buttons = [];
    const nextPage = this.props.maxPageButtons + this.state.cursorStart;
    if (this.state.cursorStart > 0) {
      buttons.push(
        <Li key="first-page">
          <Button onClick={this.handleFirstPage}>1</Button>
        </Li>,
      );
      buttons.push(
        <Li key="ellipises-previous">
          <Button onClick={() => this.handleEllipsesPrevious()}>…</Button>
        </Li>,
      );
    }
    for (
      let i = this.state.cursorStart + 1;
      i <= Math.min(nextPage, this.props.totalPages);
      i += 1
    ) {
      if (i !== this.props.totalPages) {
        buttons.push(
          <Li key={i}>
            <Button
              className={this.state.currentPage === i ? 'activePage' : ''}
              onClick={() => this.handlePageChange(i)}
            >
              {i}
            </Button>
          </Li>,
        );
      }
    }
    if (nextPage < this.props.totalPages) {
      buttons.push(
        <Li key="ellipises-next">
          <Button onClick={() => this.handleEllipsesNext(nextPage)}>…</Button>
        </Li>,
      );
    }
    buttons.push(
      <Li key="last-page">
        <Button
          className={
            this.state.currentPage === this.props.totalPages ? 'activePage' : ''
          }
          onClick={this.handleLastPage}
        >
          {this.props.totalPages}
        </Button>
      </Li>,
    );
    return buttons;
  }

  render() {
    const pages = Array(Math.max(0, this.props.totalPages))
      .fill()
      .map((e, i) => i + 1);
    const handlers = {
      previousPage: this.handlePreviousPage,
      nextPage: this.handleNextPage,
    };

    return (
      <If condition={this.props.totalPages >= 1}>
        <HotKeys
          style={{ outline: 'none' }}
          focused
          attach={window}
          keyMap={keyboardMap}
          handlers={handlers}
        >
          <Ul className="pagination">
            <Li>
              <Button
                disabled={this.state.currentPage === 1}
                onClick={this.handlePreviousPage}
              >
                ‹
              </Button>
            </Li>
            <Choose>
              <When
                condition={
                  this.props.totalPages - this.props.maxPageButtons > 1
                }
              >
                {this.arrayofPageButtons()}
              </When>
              <Otherwise>
                <For each="page" of={pages} index="i">
                  <Li key={i}>
                    <Button
                      className={
                        this.state.currentPage === i + 1 ? 'activePage' : ''
                      }
                      onClick={() => this.handlePageChange(i + 1)}
                    >
                      {i + 1}
                    </Button>
                  </Li>
                </For>
              </Otherwise>
            </Choose>
            <Li>
              <Button
                disabled={this.state.currentPage === this.props.totalPages}
                onClick={this.handleNextPage}
              >
                ›
              </Button>
            </Li>
          </Ul>
        </HotKeys>
      </If>
    );
  }
}

export default Pagination;
